import React from "react";
import { Line } from "react-chartjs-2";
import { useGlobalSettings } from "../context/GlobalSettingsContext";
import { useCurrencyFormatter } from "../hooks/useCurrencyFormatter";
import MortgageSimulator from "../simulator";

export default function SimulationChart({ sim }: { sim: MortgageSimulator }) {
  const { settings } = useGlobalSettings();
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== "undefined" && window.innerWidth < 768,
  );

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const currencySymbols: Record<string, string> = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };
  const currencySymbol =
    currencySymbols[settings.currency] || settings.currency;

  const c = useCurrencyFormatter();

  const ledger = sim.mortgage.ledger.entries;
  const requiredMonths = (sim.requiredMonths ?? ledger.length) + 1;
  const labels = Array.from({ length: requiredMonths }, (_, i) => i);

  // Mortgage balance, padded with zeros if needed
  const mortgageBalance = [
    ...ledger.map((e: any) => e.principal),
    ...Array(Math.max(0, requiredMonths - ledger.length)).fill(0),
  ];

  // Savings account lines
  const savingsDatasets = Object.entries(sim.savingsTransactions)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([idxStr, transactions]) => {
      const idx = Number(idxStr);
      const data = transactions.map((tx) => tx.balanceAfter);
      // Prepend null to shift savings by one index (align end-of-month savings with start-of-month mortgage)
      // Then pad with last value if needed
      const paddedData = [
        null,
        ...data,
        ...Array(Math.max(0, requiredMonths - data.length - 1)).fill(
          data[data.length - 1] || 0,
        ),
      ];
      return {
        label:
          idx === -1
            ? "ACR Reserve"
            : sim.savingsAccounts[idx]?.name || `Unnamed Account ${idx + 1}`,
        data: paddedData,
        pointRadius: 0,
        fill: false,
        tension: 0.1,
      };
    });

  // Calculate appropriate year interval for x-axis
  const totalYears = Math.ceil(requiredMonths / 12);
  let yearStep = 1;

  if (isMobile) {
    if (totalYears > 65) {
      yearStep = 10;
    } else if (totalYears > 50) {
      yearStep = 7;
    } else if (totalYears > 18) {
      yearStep = 5;
    } else if (totalYears > 5) {
      yearStep = 2;
    }
  } else {
    if (totalYears > 30) {
      yearStep = 5;
    } else if (totalYears > 15) {
      yearStep = 2;
    }
  }

  // Y-axis: show only a few major currency values
  const maxY = Math.max(
    ...mortgageBalance,
    ...savingsDatasets.flatMap((ds) =>
      ds.data.filter((v): v is number => v !== null),
    ),
  );
  const yStep = maxY > 200000 ? 20000 : 10000;
  const yTicks = [];
  for (let v = 0; v <= maxY; v += yStep) {
    yTicks.push(v);
  }

  const data = {
    labels: labels,
    datasets: [
      {
        label: "Mortgage Balance",
        data: mortgageBalance,
        pointRadius: 0,
        fill: false,
        tension: 0.1,
      },
      ...savingsDatasets,
    ],
  };

  const options = {
    responsive: true,
    layout: {
      padding: {
        right: 20,
      },
    },
    animation: {
      duration: 0,
    },
    interaction: {
      intersect: false,
      mode: "index" as const,
    },
    plugins: {
      legend: {
        display: savingsDatasets.length > 0,
        position: "top" as const,
      },
      title: {
        display: false,
      },
      tooltip: {
        position: "nearest" as const,
        yAlign: "bottom" as const,
        xAlign: "center" as const,
        callbacks: {
          title: function (tooltip: any) {
            let date;
            let year = Math.floor(tooltip[0].parsed.x / 12);
            let month = tooltip[0].parsed.x % 12;
            if (year > 0) {
              date = `${year} year${year === 1 ? "" : "s"} and ${month} month${month === 1 ? "" : "s"} elapsed`;
            } else {
              date = `${month} month${month === 1 ? "" : "s"} elapsed`;
            }
            return date;
          },
          label: function (context: any) {
            let label = context.dataset.label || "";

            if (label) {
              label += ": ";
            }

            if (context.parsed.y) {
              label += c(context.parsed.y);
            } else {
              label += `${currencySymbol}0`;
            }
            return label;
          },
          footer: function (tooltip: any) {
            const mortgage =
              tooltip.find((t: any) => t.dataset.label === "Mortgage Balance")
                ?.parsed.y || 0;
            const savings =
              tooltip.find(
                (t: any) => t.dataset.label === "Savings & Investments",
              )?.parsed.y || 0;
            const net = mortgage - savings;
            return `Net: ${c(net)}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value: any) {
            if (value === 0) return `${currencySymbol}0`;
            if (value >= 1000)
              return `${currencySymbol}${(value / 1000).toFixed(0)}k`;
            return `${currencySymbol}${value}`;
          },
          values: yTicks,
          font: { size: 12 },
          maxTicksLimit: 20,
        },
        grid: { display: false },
      },
      x: {
        ticks: {
          font: { size: 12 },
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          callback: function (value: any) {
            const monthIndex = Number(value);
            // Only show ticks at whole years (month 0)
            if (monthIndex % 12 === 0) {
              const year = Math.floor(monthIndex / 12) + 1;
              // Only show at yearStep intervals
              if ((year - 1) % yearStep === 0 || year === 1) {
                return `Y${year}`;
              }
            }
            return "";
          },
        },
        grid: { display: false },
      },
    },
  };

  return <Line data={data} options={options} />;
}
